/* cart-items.js - every mutation a shopper can make from inside the cart:
   quantities, line removal, discount codes, the order note. Loaded by
   cart-drawer.js, so it is never on the critical path either.

   Two rules shape the whole file.

   1. The answer to a mutation is Liquid's, not this script's. Every request
      carries the Ajax API's `sections` parameter and the host surface is
      repainted from the HTML that comes back, so money formatting, plural forms,
      discount titles and the free shipping bar stay in the locale files and in
      the snippets. Recomputing any of them here is how a drawer starts
      disagreeing with the checkout it links to.

   2. The optimistic write is rolled back and the refusal is SHOWN. A quantity
      the API declines (stock, a limit, a dead network) restores the field to
      the value it had and prints the reason in the drawer's alert region. An
      error the shopper cannot see is a cart that silently lies about itself.

   3. A removal is offered back, and only when it can be kept. The row hands
      over a variant id, a quantity and a server composed sentence; the notice
      in snippets/cart-undo.liquid holds them for a few seconds and /cart/add.js
      puts the line back. The row withholds all three when the line carries
      properties or a selling plan. Not because the API refuses them: the
      documented add takes `properties` and `selling_plan` too. Because the
      payload travels through the DOM, and a property whose name begins with an
      underscore is hidden from the customer and therefore printed nowhere in
      this markup, so the row could not hand over what it never showed. An undo
      that returns something else is worse than no undo at all.

   Every listener is delegated on `document`, deliberately: rule 1 replaces the
   host's innerHTML on every mutation, so a listener bound to a button would be
   pointing at a detached node one change later. That is also what lets the same
   file drive the drawer AND the cart page with no second binding pass: the only
   thing it knows about either is `data-cart-root`. */
(function () {
  'use strict';

  var PP = window.PP;
  if (!PP) return;

  if (PP.cartItemsLoaded) return;
  PP.cartItemsLoaded = true;

  /* The Section Rendering API keys off the id in the section group or in the
     JSON template, not the file name. Read it back from the element the section
     itself rendered, so renaming the key in overlay-group.json or in
     templates/cart.json cannot silently stop the repaint: the request would
     still succeed and simply return the section absent. */
  function idOf(node) {
    return (node && node.getAttribute('data-section-id')) || 'cart-drawer';
  }

  /* EVERY cart root on the page, comma joined, because /cart carries two: the
     page section and the drawer that the header link can still open over it.
     Repainting only the surface the shopper touched would leave the other one
     quoting quantities the cart no longer holds. The Ajax API takes a comma
     separated list and answers with one entry per section, so the second
     surface costs one render and no second request. */
  function sectionList() {
    var ids = [];
    roots().forEach(function (node) {
      var id = idOf(node);
      if (ids.indexOf(id) === -1) ids.push(id);
    });
    return ids.join(',') || 'cart-drawer';
  }

  var NOTE_DELAY = 700;
  var noteTimer = null;
  var pending = 0;

  /* How long the undo offer stands. A reading and reaching time rather than a
     round number: long enough to notice a sentence appear, read it and travel
     to a button on a phone, short enough that it is plainly about the thing
     that just happened. The clock is paused, not merely reset, while the
     keyboard is inside the notice; see expireUndo(). */
  var UNDO_WINDOW = 8000;
  var undoTimer = null;
  var undoPayload = null;

  /* THE HOST is whichever cart surface the shopper is standing in. Both the
     drawer's dialog and the cart page's section carry `data-cart-root`, and
     this file knows nothing else about either of them.

     On /cart BOTH exist, so an OPEN dialog wins: the shopper is in the panel,
     and reading the page underneath it would answer about a surface nobody is
     looking at.

     The fallback SKIPS a closed dialog instead of taking the first root in
     document order. Document order is only right today because
     layout/theme.liquid renders the overlay group after content_for_layout, and
     one reordered section group would quietly hand the host to a panel nobody
     can see. The last resort is still the first root: on every route but /cart
     the closed drawer is the only cart there is, and answering nothing would
     strand the one surface that exists. */
  function host() {
    var open = document.querySelector('dialog[data-cart-root][open]');
    if (open) return open;
    var list = roots();
    for (var i = 0; i < list.length; i++) {
      if (list[i].tagName !== 'DIALOG' || list[i].open) return list[i];
    }
    return list[0] || null;
  }

  function roots() {
    return Array.prototype.slice.call(document.querySelectorAll('[data-cart-root]'));
  }

  /* Strings script shows a shopper come from the locale files by way of data
     attributes on the host element, which is why every host has to stamp the
     same set. A message written here could not be translated. */
  function label(name) {
    var node = host();
    return (node && node.getAttribute(name)) || '';
  }

  function root() {
    return (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
  }

  function post(endpoint, body, withSections) {
    if (withSections) {
      body.sections = sectionList();
      body.sections_url = window.location.pathname;
    }
    var config = PP.fetchConfig('json');
    config.body = JSON.stringify(body);
    /* The body is parsed DEFENSIVELY because this endpoint does not always
       answer with JSON: /cart/add.js returns an HTML interstitial on a 429 or a
       502, which is a live risk on the undo path since a shopper who removes
       and restores repeatedly is exactly what the platform throttles. Letting
       response.json() reject would surface the parser's own message, so the
       shopper would read "Unexpected token <" where the theme has a translated
       sentence ready. An unparseable body is simply no description, and
       showError falls back to data-cart-error-text. */
    return fetch(root() + endpoint, config).then(function (response) {
      return response
        .json()
        .catch(function () {
          return null;
        })
        .then(function (data) {
          if (response.ok && data) return data;
          var error = new Error((data && (data.description || data.message)) || '');
          error.data = data;
          throw error;
        });
    });
  }

  /* ---- feedback ---------------------------------------------------------- */

  /* EVERY root, never the current one. `pending` is a single counter, but
     host() moves the instant the drawer opens: a shopper who steps a quantity
     on /cart and opens the panel before the answer lands would have the busy
     state put on the section and taken off the dialog, and main-cart.liquid
     holds a busy layout at pointer-events: none, so the page would stay inert
     until a reload. Writing all of them is race free AND shorter than choosing.
     A message written into a closed dialog is invisible and costs nothing; a
     message written into the other surface is a shopper who never sees the
     refusal at all. */
  function showError(message) {
    /* Unhide BEFORE writing: a live region mutated while it is still hidden
       is a mutation several screen readers never announce. */
    var text = message || label('data-cart-error-text');
    roots().forEach(function (node) {
      var box = node.querySelector('[data-cart-error]');
      if (!box) return;
      box.hidden = !text;
      box.textContent = text;
    });
  }

  function clearError() {
    roots().forEach(function (node) {
      var box = node.querySelector('[data-cart-error]');
      if (!box) return;
      box.textContent = '';
      box.hidden = true;
    });
  }

  function busy(on) {
    pending += on ? 1 : -1;
    if (pending < 0) pending = 0;
    var running = pending > 0;
    var text = running ? label('data-cart-updating-text') : '';
    roots().forEach(function (node) {
      if (running) node.setAttribute('aria-busy', 'true');
      else node.removeAttribute('aria-busy');
      var status = node.querySelector('[data-cart-status]');
      if (status) status.textContent = text;
    });
  }

  /* ---- repaint ----------------------------------------------------------- */

  /* A line key is platform data, not ours to trust the shape of: escape what
     could end the attribute string early. And a selector rebuilt from live DOM
     is still a guess, so a bad one must cost the focus target, never the rest
     of the restore. */
  function esc(value) {
    return String(value).replace(/\\/g, '\\\\').replace(/"/g, '\\"');
  }

  function find(node, selector) {
    if (!selector) return null;
    try {
      return node.querySelector(selector);
    } catch (e) {
      return null;
    }
  }

  /* What has to survive an innerHTML swap: which disclosure was open, an
     unsaved note, and where the keyboard was. Without the last one a shopper
     stepping a quantity with the keyboard is dropped onto the body on the
     first press and has to tab back in for the second. */
  function selectorFor(el) {
    if (!el || !el.matches) return null;
    if (el.matches('[data-quantity-field]')) {
      return '[data-quantity-field][data-line-key="' + esc(el.getAttribute('data-line-key')) + '"]';
    }
    if (el.matches('[data-quantity-step]')) {
      var line = el.closest('[data-cart-line]');
      if (!line) return null;
      return (
        '[data-cart-line][data-line-key="' +
        esc(line.getAttribute('data-line-key')) +
        '"] [data-quantity-step="' +
        esc(el.getAttribute('data-quantity-step')) +
        '"]'
      );
    }
    /* The remove control is keyed like the quantity field, and after a removal
       that key is gone from the repainted markup on purpose: restore() reads
       the miss as "the line the keyboard was on no longer exists" and moves on
       to the next one, instead of dropping the shopper on the root. */
    if (el.matches('[data-line-remove]')) {
      return '[data-line-remove][data-line-key="' + esc(el.getAttribute('data-line-key')) + '"]';
    }
    if (el.matches('[data-discount-field]')) return '[data-discount-field]';
    if (el.matches('[data-cart-note]')) return '[data-cart-note]';
    return null;
  }

  function snapshot() {
    var node = host();
    if (!node) return null;
    var active = document.activeElement;
    var note = node.querySelector('[data-cart-note]');
    var open = [];
    Array.prototype.forEach.call(node.querySelectorAll('details[open]'), function (el) {
      if (el.className) open.push(el.className);
    });
    /* WHETHER the keyboard was in here is a separate question from WHERE, and
       restore() needs both. selectorFor() returns null for an element it does
       not recognise as well as for an element that was never in this root, and
       those two deserve opposite answers: the first should still land
       somewhere, the second must not be touched at all. */
    var inside = !!(active && node.contains(active));
    return {
      open: open,
      note: note ? note.value : null,
      hadFocus: inside,
      selector: inside ? selectorFor(active) : null
    };
  }

  function restore(state) {
    var node = host();
    if (!node || !state) return;
    state.open.forEach(function (className) {
      var el = find(node, 'details.' + className.trim().split(/\s+/).join('.'));
      if (el) el.open = true;
    });
    var note = node.querySelector('[data-cart-note]');
    if (note && state.note != null && state.note !== note.value) {
      /* The text differs from what the server just rendered, which is what
         makes it unsaved. The timer that would have saved it was armed on the
         textarea this repaint detached, and its isConnected guard turns it into
         a no-op, so the note the shopper can still SEE would never reach the
         order. Re-arm it on the node that replaced it. saveNote posts without
         `sections`, so nothing it does comes back through repaint and the
         arming cannot feed itself. */
      note.value = state.note;
      armNote(note);
    }
    /* A CLOSED dialog must not steal the focus, and NEITHER MUST A CLOSING ONE.
       `open` stays true for the whole 600ms of the close slide, so a repaint
       landing in that window (step a quantity, then tap the scrim before the
       answer arrives) would put the keyboard on a control inside a panel that
       is already leaving, scrolling that panel to it on the way out, only for
       cart-drawer.js's finish() to move focus to the trigger a moment later.
       cart-drawer.js marks the slide with the attribute for exactly this.

       Both tests are asked only OF a dialog: the cart page's section has no
       `open` property, would read undefined, and the restore would be skipped
       on the one surface with no other way back to the keyboard. */
    if (node.tagName === 'DIALOG' && (!node.open || node.hasAttribute('data-drawer-closing'))) return;
    var target = find(node, state.selector);
    /* ONLY FOR A SHOPPER WHO ALREADY HAD THE KEYBOARD IN HERE. Focus the cart
       never held is not the cart's to move: on Safari and Firefox for macOS a
       mouse click on a button leaves activeElement on the body, so every click
       on a stepper reaches this line with nothing snapshotted, and running the
       chain there would yank the shopper to the first line and scroll them to
       it. `hadFocus` is the gate rather than the selector being null, because a
       selector can also be null for an element inside this root that
       selectorFor() simply does not recognise, and that one does want a landing.

       Given the gate, in order: the first remove control still standing, then
       the empty cart's own way out, then the drawer's close button. The root is
       the LAST resort because it is an unlabelled container that announces
       nothing on arrival; main-cart.liquid no longer blanks its focus ring, so
       at least the landing is visible when it happens. When none of them is
       there the focus is LEFT WHERE IT IS: a missing fallback must not throw
       and must not blank the focus. */
    if (!target && state.hadFocus) {
      target =
        node.querySelector('[data-line-remove]') ||
        node.querySelector('[data-cart-continue]') ||
        node.querySelector('[data-cart-drawer-close]') ||
        (node.hasAttribute('tabindex') ? node : null);
    }
    if (target) target.focus();
  }

  /* THE FREE SHIPPING BAR FILLS, and a width transition needs an element that
     existed at the old value. --pp-progress only ever arrives as an inline
     style Liquid wrote on the bar, and the swap below destroys the element that
     carried it, so the incoming bar is pinned to the OUTGOING value for one
     frame and only then released to the value the server rendered. That is the
     only point at which the browser has two widths to interpolate between.

     Duplicated in assets/cart-drawer.js rather than shared: this file drives
     /cart where that one may never load, and that one can run before this one
     has finished loading, so neither can own the helper for the other without
     moving cart code into the core bundle every route pays for. */
  function progressOf(node) {
    var bar = node && node.querySelector('[data-free-shipping]');
    /* Trimmed because a custom property keeps the whitespace it was written
       with, and `--pp-progress: 40%` reads back on some engines with the
       leading space still on it. The equality test below is the whole guard
       against animating a bar that did not move. */
    return bar ? bar.style.getPropertyValue('--pp-progress').trim() : '';
  }

  function fillFrom(node, from) {
    if (!from || PP.reducedMotion()) return;
    var bar = node && node.querySelector('[data-free-shipping]');
    var fill = bar && bar.querySelector('[data-free-shipping-fill]');
    if (!bar || !fill) return;
    var target = bar.style.getPropertyValue('--pp-progress').trim();
    if (!target || target === from) return;
    /* THE PIN IS MADE WITH THE TRANSITION OFF, and that is not belt and braces.
       Whether the browser has already resolved this fill's style once by the
       time we get here depends on what ran in between: a freshly parsed element
       has no previous style and would not transition, but a focus() or an
       offsetWidth read anywhere earlier in the same task forces a recalculation
       first, and then pinning to the OUTGOING value would animate the bar
       BACKWARDS before it animated forwards. Suppressing the transition for the
       one frame that establishes the start value removes the question.

       The reflow is read off the fill rather than the root because the fill is
       the element the transition is declared on, and it is the one whose
       computed width has to be committed before the transition comes back. */
    fill.style.transition = 'none';
    bar.style.setProperty('--pp-progress', from);
    void fill.offsetWidth;
    fill.style.removeProperty('transition');
    requestAnimationFrame(function () {
      if (bar.isConnected) bar.style.setProperty('--pp-progress', target);
    });
  }

  /* ---- what the free shipping bar is allowed to feel ----------------------

     THE BAR ARRIVES NEW ON EVERY REPAINT, so nothing on the element can know
     what it replaced, and snippets/free-shipping-bar.liquid is written to be
     the correct RESTING state only: right words, right colour, right width.
     Which of those rested states deserved a movement is decided here, from
     one memory per root of the last bar this module saw: whether it had
     reached the threshold, and what its sentence said.

     It runs off the cart bus rather than inside repaint(), because two
     painters swap this markup and both publish PP.EVENTS.cartUpdate AFTER
     the swap: repaint() above through announce(), and cart-drawer.js's
     paint() for the add paths. One subscriber therefore covers a quantity
     step in the drawer, a quantity step on /cart, and an add from a product
     page, without either painter learning anything new.

     Three outcomes, all classes the snippet's stylesheet animates:
       .is-fresh on the label     the sentence changed. A step that leaves the
                                  words alone must not blink them.
       .is-unlocked on the root   this repaint crossed the threshold UPWARD.
                                  Once: the next repaint above the line finds
                                  the memory already `reached` and adds nothing.
       the colour pin             the state attribute crossed either way, so
                                  the incoming root is held at the OUTGOING
                                  state for one frame with the transition off
                                  and released in the next, exactly as
                                  fillFrom() holds the width. Both pins release
                                  in the same frame, so width and colour move
                                  together.

     REDUCED MOTION skips all three after the memory is updated: the markup
     already says the right thing in the right colour, and what is skipped is
     only the movement. The bar's own stylesheet also sets the transition and
     both animations to none under the same query, so a class that did slip
     through would still not move. */
  var barMemory = {};

  function barOf(node) {
    var bar = node && node.querySelector('[data-free-shipping]');
    if (!bar) return null;
    var label = bar.querySelector('[data-free-shipping-label]');
    return {
      bar: bar,
      label: label,
      reached: bar.getAttribute('data-free-shipping-state') === 'reached',
      text: label ? label.textContent.replace(/\s+/g, ' ').trim() : ''
    };
  }

  function rememberBars() {
    roots().forEach(function (node) {
      var now = barOf(node);
      if (now) barMemory[idOf(node)] = { reached: now.reached, text: now.text };
    });
  }

  function feelBars() {
    roots().forEach(function (node) {
      var now = barOf(node);
      if (!now) return;
      var key = idOf(node);
      var was = barMemory[key];
      barMemory[key] = { reached: now.reached, text: now.text };
      if (!was || PP.reducedMotion()) return;

      if (now.reached !== was.reached) {
        var fill = now.bar.querySelector('[data-free-shipping-fill]');
        var from = was.reached ? 'reached' : 'remaining';
        var to = now.reached ? 'reached' : 'remaining';
        if (fill) {
          fill.style.transition = 'none';
          now.bar.setAttribute('data-free-shipping-state', from);
          void fill.offsetWidth;
          fill.style.removeProperty('transition');
          requestAnimationFrame(function () {
            if (now.bar.isConnected) now.bar.setAttribute('data-free-shipping-state', to);
          });
        }
        if (now.reached) {
          now.bar.classList.add('is-unlocked');
          return;
        }
      }
      if (now.label && now.text !== was.text) now.label.classList.add('is-fresh');
    });
  }

  /* Sections that fail to render come back as null with an HTTP 200, so a
     successful request can still leave nothing to paint. Dropping the busy
     state anyway is what keeps the lines from staying dimmed and inert. */
  function release(node) {
    if (!node) return;
    Array.prototype.forEach.call(node.querySelectorAll('[data-cart-line][aria-busy]'), function (line) {
      line.removeAttribute('aria-busy');
      /* A line that was on its way out and is still here has to come back:
         cart-line.liquid holds `--leaving` at zero opacity, so leaving the
         class on a row the repaint failed to replace would erase it from the
         cart on screen while it is still in the cart on the server. */
      line.classList.remove('cart-line--leaving');
    });
  }

  /* Returns the item count the server just rendered, or null when no root came
     back with one.

     WHY THE COUNT TRAVELS ON THE MARKUP. /cart/add.js answers with the LINE IT
     ADDED and no cart total (Ajax Cart API: "the response is a JSON object of
     the line items associated with the added items"), so the undo's re-add has
     no item_count to publish and assets/header.js would leave the badge showing
     the number it had before the row came back. Every host stamps
     data-cart-item-count from Liquid, so the render already paid for carries
     the answer. assets/cart-drawer.js does exactly this for the same reason.

     READ off the parsed node and WRITTEN back onto the live one, because the
     swap below copies CHILDREN: a root's own attributes are never touched by a
     repaint, so without this line the drawer would keep advertising the count
     it was rendered with at page load and the next reader would believe it. */
  function repaint(data) {
    var sections = (data && data.sections) || {};
    var count = null;
    /* Snapshot FIRST: it reads document.activeElement, and the first swap below
       detaches whatever that was. */
    var state = snapshot();
    roots().forEach(function (node) {
      var html = sections[idOf(node)];
      if (!html) {
        release(node);
        return;
      }
      var next = new DOMParser().parseFromString(html, 'text/html').querySelector('[data-cart-root]');
      if (!next) {
        release(node);
        return;
      }
      var fresh = next.getAttribute('data-cart-item-count');
      if (fresh !== null && fresh !== '') {
        node.setAttribute('data-cart-item-count', fresh);
        count = fresh;
      }
      var was = progressOf(node);
      /* A HOST MAY NOMINATE AN INNER ELEMENT THAT HAS TO SURVIVE. The drawer
         does: its panel is the element that carries the open and close slide,
         and replacing the root's innerHTML mid slide detaches the very node the
         transition is running on. A detached node never fires transitionend, so
         cart-drawer.js would fall through to its last resort timeout with the
         content already gone, the dialog still in the top layer eating clicks
         and the page still locked against scrolling. Swapping inside the panel
         costs nothing anywhere else and loses nothing here, because the panel
         is the dialog's only child. /cart nominates nothing and takes the
         whole swap. */
      var into = node.querySelector('[data-cart-drawer-panel]');
      var from = next.querySelector('[data-cart-drawer-panel]');
      if (into && from) into.innerHTML = from.innerHTML;
      else node.innerHTML = next.innerHTML;
      fillFrom(node, was);
    });
    restore(state);
    return count;
  }

  /* `cart` is null unless the response WAS a cart, which /cart/add.js's is not.
     Publishing a line item under that name would read as a cart to every
     subscriber and answer every question about it wrongly. The number is the
     one thing both shapes can supply, from the response when it carries a
     total and from the markup the same request rendered when it does not. */
  function announce(data, count) {
    var cart = data && data.item_count != null ? data : null;
    var total = cart ? cart.item_count : null;
    if (total == null && count != null) total = parseInt(count, 10);
    PP.publish(PP.EVENTS.cartUpdate, {
      cart: cart,
      item_count: total,
      source: 'cart-items'
    });
  }

  function fail(error) {
    showError(error && error.message ? error.message : '');
    PP.publish(PP.EVENTS.cartError, { error: error });
  }

  /* ---- undo -------------------------------------------------------------- */

  /* REMOVING A LINE IS ONE TAP, next to the stepper, on a phone. It is the
     easiest gesture in this interface to make by accident and, until now, the
     only irreversible one.

     THE OFFER IS ONLY MADE WHEN IT CAN BE KEPT, and the limit is OURS rather
     than the platform's: /cart/add.js accepts `properties` and `selling_plan`
     as well as an id and a quantity (Ajax Cart API, "Add line item properties"
     and "Add a selling plan"). What cannot carry them is this route. The
     payload is three data attributes on the row, and a property whose name
     begins with an underscore is hidden from the customer, so cart-line.liquid
     prints it nowhere and there is nothing here to read. A line put back from a
     payload that dropped them would be a DIFFERENT line: the personalisation
     gone, the subscription turned into a one off, under a button that said
     "Undo". snippets/cart-line.liquid decides that on the server, where those
     properties are still visible, and simply withholds the payload attributes.
     No payload, no offer. That is why this reads three attributes instead of
     testing anything itself.

     WHAT IS NOT RESTORED, said plainly because a promise is what this is:
     /cart/add.js appends, so a line put back from the middle of a long cart
     returns at the end. The contents are identical; the position is not. */
  function payloadFor(line) {
    if (!line) return null;
    var id = parseInt(line.getAttribute('data-line-undo-id'), 10);
    var quantity = parseInt(line.getAttribute('data-line-undo-quantity'), 10);
    if (!id || !(quantity > 0)) return null;
    return { id: id, quantity: quantity, label: line.getAttribute('data-line-undo-label') || '' };
  }

  function undoBoxes() {
    var list = [];
    roots().forEach(function (node) {
      var box = node.querySelector('[data-cart-undo]');
      if (box) list.push(box);
    });
    return list;
  }

  function shutUndo(box) {
    box.hidden = true;
    var text = box.querySelector('[data-cart-undo-label]');
    if (text) text.textContent = '';
  }

  /* Immediate and unconditional: this is the hide a second tap on the same
     button must not be able to survive, and the hide any new mutation owes the
     shopper, because an undo is about the last thing that happened and that
     just stopped being true. */
  function hideUndo() {
    clearTimeout(undoTimer);
    undoPayload = null;
    undoBoxes().forEach(shutUndo);
  }

  /* The window running out, which is a different question. A notice taken away
     while the keyboard is standing in it drops the shopper on the body with no
     announcement and nothing focused, so the clock waits for the focus to leave
     and retires then. The offer stays honest in the meantime: nothing about the
     cart has changed while it stood. */
  function expireUndo() {
    clearTimeout(undoTimer);
    var held = null;
    undoBoxes().forEach(function (box) {
      if (box.hidden) return;
      if (box.contains(document.activeElement)) {
        held = box;
        return;
      }
      shutUndo(box);
    });
    if (held) {
      held.addEventListener('focusout', expireUndo, { once: true });
      return;
    }
    undoPayload = null;
  }

  /* Written into EVERY root for the reason showError is: the shopper may have
     removed a line on /cart and opened the drawer over it before the answer
     landed, and the surface they are looking at has to be the one holding the
     offer. A closed dialog is display:none, so the copy inside it is neither
     seen nor announced and costs nothing. */
  function offerUndo(payload) {
    undoPayload = payload;
    undoBoxes().forEach(function (box) {
      var text = box.querySelector('[data-cart-undo-label]');
      /* Unhide BEFORE writing, as the alert does: a live region mutated while
         it is still hidden is a mutation several screen readers never make. */
      box.hidden = false;
      if (text) text.textContent = payload.label;
    });
    clearTimeout(undoTimer);
    undoTimer = setTimeout(expireUndo, UNDO_WINDOW);
  }

  /* The keyboard was on a button that is about to stop existing. Park it on
     something inside the same root so that the repaint's own snapshot sees a
     shopper who had focus in here, and restore() lands them on the first remove
     control the way it does after any other line disappears. Anything else
     leaves them on the body, one screen from where they were working. */
  function parkFocus() {
    var node = host();
    if (!node) return;
    var park =
      node.querySelector('[data-cart-drawer-close]') ||
      (node.hasAttribute('tabindex') ? node : null);
    if (park) park.focus();
  }

  function addBack(payload) {
    busy(true);
    clearError();
    post('cart/add.js', { id: payload.id, quantity: payload.quantity }, true)
      .then(function (data) {
        var count = repaint(data);
        announce(data, count);
      })
      .catch(fail)
      .then(function () {
        busy(false);
      });
  }

  /* ---- quantities and lines ---------------------------------------------- */

  function change(key, quantity, field, previous, line, undo) {
    busy(true);
    clearError();
    hideUndo();
    /* The payload was READ at the click, while the row still existed to be read
       from, but it is PUBLISHED only here, when this removal is known to have
       landed. Two quick removes would otherwise leave the notice naming the
       second product while the first request was still in flight, and a refused
       removal would offer to undo something that never happened. */
    var landed = null;
    post('cart/change.js', { id: key, quantity: quantity }, true)
      .then(function (data) {
        var count = repaint(data);
        announce(data, count);
        landed = undo || null;
      })
      .catch(function (error) {
        /* ROLLBACK. The field is still on screen because nothing was
           repainted, so putting the old number back is the whole undo. */
        if (field && field.isConnected) {
          field.value = previous;
          syncFloor(field);
        }
        if (line && line.isConnected) {
          line.removeAttribute('aria-busy');
          line.classList.remove('cart-line--leaving');
        }
        fail(error);
      })
      .then(function () {
        busy(false);
        /* AFTER busy(false), so the two announcements do not collide: that call
           blanks the progress region in the same task this one fills the
           notice, and a screen reader handed both at once reads whichever it
           reaches first. */
        if (landed) offerUndo(landed);
      });
  }

  /* THE FLOOR IS READ, NOT ASSUMED. snippets/quantity-input.liquid prints it as
     the field's `min`: 0 on a cart line, because typing 0 is how a line is
     removed, and 1 on a product form, because adding none of something is not
     an order. Defaulting to 0 keeps a field with no min attribute behaving
     exactly as the cart always has. */
  function floorOf(field) {
    var floor = parseInt(field.getAttribute('min'), 10);
    return isNaN(floor) ? 0 : floor;
  }

  /* THE DECREMENT BUTTON SAYS NO BEFORE THE PRESS. The snippet server renders
     `disabled` when the quantity already sits on the floor, so the state is
     right on first paint; this keeps that same answer true for every value the
     field takes afterwards, and both paths below run through it. The increment
     side is never touched: there is no maximum to reach on it. */
  function syncFloor(field) {
    var wrap = field.closest ? field.closest('[data-quantity]') : null;
    var down = wrap && wrap.querySelector('[data-quantity-step="-1"]');
    if (!down) return;
    down.disabled = (parseInt(field.value, 10) || 0) <= floorOf(field);
  }

  function step(button) {
    var wrap = button.closest('[data-quantity]');
    var field = wrap && wrap.querySelector('[data-quantity-field]');
    if (!field) return;
    var by = parseInt(button.getAttribute('data-quantity-step'), 10) || 0;
    var previous = field.value;
    var floor = floorOf(field);
    var next = (parseInt(previous, 10) || floor) + by;
    if (next < floor) next = floor;
    apply(field, next, previous);
  }

  function apply(field, next, previous) {
    var key = field.getAttribute('data-line-key');
    if (!key) {
      /* NO LINE KEY, NO REQUEST, AND THAT IS THE WHOLE BRANCH. This field is
         not a cart line: it is a plain form control on a product form that the
         shopper is about to submit, so the number simply moves and there is
         nothing to POST. onClick has already prevented the press, so returning
         without moving it -- which is what this function used to do -- was a
         dead button, and the reason the product form carried a second stepper
         with its own hooks. */
      field.value = next;
      syncFloor(field);
      return;
    }
    /* Optimistic: the number moves now, the line greys out, the request
       follows. cart-line.liquid styles [aria-busy] as dimmed and inert. */
    field.value = next;
    syncFloor(field);
    var line = field.closest('[data-cart-line]');
    if (line) line.setAttribute('aria-busy', 'true');
    change(key, next, field, previous, line);
  }

  /* ---- discounts --------------------------------------------------------- */

  /* /cart/update.js REPLACES the whole set rather than adding to it, so both
     paths post the full list the cart should end up with. An empty string is
     how the last code is removed.

     Document wide, deliberately NOT scoped to host(): a discount form drawn on
     one surface only would make the other one answer with an empty list, and an
     empty list is the instruction to wipe every applied code. The price of
     asking the whole document is that a code drawn on both surfaces is counted
     twice, so the set is deduped by value before it is posted. */
  function codes() {
    var list = [];
    Array.prototype.forEach.call(document.querySelectorAll('[data-discount-remove]'), function (el) {
      var code = el.getAttribute('data-discount-remove');
      if (list.indexOf(code) === -1) list.push(code);
    });
    return list;
  }

  function discount(list, applied) {
    busy(true);
    clearError();
    hideUndo();
    post('cart/update.js', { discount: list.join(',') }, true)
      .then(function (data) {
        var count = repaint(data);
        announce(data, count);
        if (!applied) return;
        /* The platform accepts the request and returns 200 for a code it will
           not honour. cart-discount.liquid prints only applicable codes, so
           the repainted markup is the answer: absent means refused. */
        var node = host();
        var found = node
          ? Array.prototype.some.call(node.querySelectorAll('[data-discount-remove]'), function (el) {
              return (el.getAttribute('data-discount-remove') || '').toUpperCase() === applied.toUpperCase();
            })
          : false;
        if (!found) showError(label('data-cart-discount-error-text'));
      })
      .catch(fail)
      .then(function () {
        busy(false);
      });
  }

  /* ---- note -------------------------------------------------------------- */

  /* No `sections` here on purpose: repainting the footer under a shopper who
     is still typing would take the caret with it. */
  function saveNote(field) {
    busy(true);
    post('cart/update.js', { note: field.value }, false)
      .then(function (data) {
        clearError();
        announce(data);
        var node = host();
        var flag = node && node.querySelector('[data-cart-note-status]');
        if (!flag) return;
        flag.textContent = label('data-cart-note-saved-text');
        setTimeout(function () {
          if (flag.isConnected) flag.textContent = '';
        }, 4000);
      })
      .catch(fail)
      .then(function () {
        busy(false);
      });
  }

  /* One place arms the debounce, because two of them do: typing arms it, and a
     repaint that hands the unsaved text to a new textarea has to arm it again
     on that node. Clearing first is what keeps the two from racing. */
  function armNote(field) {
    clearTimeout(noteTimer);
    noteTimer = setTimeout(function () {
      if (field.isConnected) saveNote(field);
    }, NOTE_DELAY);
  }

  /* ---- delegation -------------------------------------------------------- */

  function onClick(event) {
    var target = event.target;
    if (!target || !target.closest) return;

    var stepper = target.closest('[data-quantity-step]');
    if (stepper) {
      event.preventDefault();
      step(stepper);
      return;
    }

    var again = target.closest('[data-cart-undo-action]');
    if (again) {
      event.preventDefault();
      var restoring = undoPayload;
      var hadKeyboard = again.contains(document.activeElement) || again === document.activeElement;
      /* Hide FIRST, and unconditionally: the request has not been sent yet, so
         a second tap on a button still on screen would add the line twice. */
      hideUndo();
      if (hadKeyboard) parkFocus();
      if (restoring) addBack(restoring);
      return;
    }

    /* The remove control is an anchor to item.url_to_remove, so it still works
       with this file absent. Intercepting it is the enhancement. */
    var remove = target.closest('[data-line-remove]');
    if (remove) {
      event.preventDefault();
      var line = remove.closest('[data-cart-line]');
      /* Two states, not one. aria-busy is the shared "this row is mid request"
         dim that a quantity step also uses; `--leaving` is specific to a row
         that is going away, and only this path knows that. It rides the same
         optimism as the quantity write above: the row starts leaving now and
         the rollback in change() puts it back if the API refuses. */
      if (line) {
        line.setAttribute('aria-busy', 'true');
        line.classList.add('cart-line--leaving');
      }
      /* Read NOW, while the row is still in the document to be read from: the
         repaint that answers this request is what takes it away. */
      change(remove.getAttribute('data-line-key'), 0, null, null, line, payloadFor(line));
      return;
    }

    var drop = target.closest('[data-discount-remove]');
    if (drop) {
      event.preventDefault();
      var gone = drop.getAttribute('data-discount-remove');
      discount(
        codes().filter(function (code) {
          return code !== gone;
        }),
        null
      );
    }
  }

  function onSubmit(event) {
    var form = event.target;
    if (!form || !form.matches || !form.matches('[data-discount-form]')) return;
    var field = form.querySelector('[data-discount-field]');
    var value = field ? field.value.trim() : '';
    if (!value) return;
    event.preventDefault();
    field.value = '';
    discount(codes().concat([value]), value);
  }

  function onChange(event) {
    var field = event.target;
    if (!field || !field.matches) return;
    if (field.matches('[data-quantity-field]')) {
      /* Clamped to the FIELD'S floor, not to zero: a typed 0 is a removal on a
         cart line and a nonsense on a product form, and the markup is the only
         thing that knows which surface this is. */
      var floor = floorOf(field);
      var next = parseInt(field.value, 10);
      if (isNaN(next) || next < floor) next = floor;
      apply(field, next, field.defaultValue);
      return;
    }
    if (field.matches('[data-cart-note]')) {
      clearTimeout(noteTimer);
      saveNote(field);
    }
  }

  function onInput(event) {
    var field = event.target;
    if (!field || !field.matches || !field.matches('[data-cart-note]')) return;
    armNote(field);
  }

  /* init() is idempotent and the listeners live on `document`, which no
     innerHTML swap and no section reload can detach. shopify:section:load
     still calls it so the guard, not luck, is what makes the second call a
     no-op; shopify:section:unload releases the pending note write. */
  function init() {
    rememberBars();
    if (document.documentElement.hasAttribute('data-cart-items-ready')) return;
    document.documentElement.setAttribute('data-cart-items-ready', '');
    document.addEventListener('click', onClick);
    document.addEventListener('submit', onSubmit);
    document.addEventListener('change', onChange);
    document.addEventListener('input', onInput);
    /* After the swap, never before it: every publisher on this bus paints
       first and publishes second, which is what lets feelBars() read the
       incoming bar. */
    PP.subscribe(PP.EVENTS.cartUpdate, feelBars);
  }

  init();

  document.addEventListener('shopify:section:load', init);

  document.addEventListener('shopify:section:unload', function () {
    clearTimeout(noteTimer);
    clearTimeout(undoTimer);
    undoPayload = null;
    pending = 0;
  });
})();
